import React, { Fragment, useState } from "react";
import moment from "moment";
import { API_BASE_URL } from "../../config/apiConfig";
import {
  Badge, DETAIL_ACCENTS, DetailFields as Fields, DetailSection as Section, EmptyState as Empty, Pill,
  detailLabelStyle, detailTableWrap, detailTdStyle as tdStyle, detailThStyle as thStyle, isEmpty, show, titleCase,
} from "./DetailUI";

// Documents / Markup Rules / Bank Details sections shared by the agent and supplier detail pages
// (both detail APIs return the same documents, markups and bank_details shapes).

const linkStyle = { color: "var(--btn-bg)", textDecoration: "none" };

const formatDate = (d) => (d ? moment(d).format("DD MMM YYYY, hh:mm A") : "-");
const formatDay = (d) => (d ? moment(d).format("DD MMM YYYY") : "-");
const humanOr = (v, fallback) => (isEmpty(v) ? fallback : titleCase(v));
const isImage = (url) => /\.(png|jpe?g|gif|webp|svg)(\?|$)/i.test(url || "");
const maskAccount = (n) => (isEmpty(n) ? "-" : String(n).length <= 4 ? n : "•••• " + String(n).slice(-4));
const plural = (n, word) => `${n} ${word}${n == 1 ? "" : "s"}`;

// API returns some files as relative paths ("uploads/banks/x.png"); serve them from the API host
export const fileUrl = (url) =>
  isEmpty(url) ? null : /^https?:\/\//i.test(url) ? url : `${API_BASE_URL.replace(/\/api\/v1\/?$/, "")}/${String(url).replace(/^\//, "")}`;

export const countVerified = (documents = []) =>
  documents.filter((d) => String(d.verification_status).toLowerCase() == "verified").length;

/* ---------- Documents ---------- */
export const DocumentsSection = ({ documents = [], accent = DETAIL_ACCENTS.violet }) => {
  const verified = countVerified(documents);
  return (
    <Section
      icon="bi-file-earmark-text-fill"
      title="Documents"
      subtitle={`${plural(documents.length, "document")} · ${verified} verified`}
      accent={accent}
    >
      {documents.length > 0 ? (
        <div className="row g-3">
          {documents.map((d) => {
            const url = fileUrl(d.doc_url);
            return (
              <div key={d.id} className="col-12 col-md-6 col-xl-4">
                <div className="h-100 d-flex flex-column" style={{ border: "1px solid #eef1f7", borderRadius: 12, overflow: "hidden", background: "#fff" }}>
                  <a
                    href={url || undefined}
                    target="_blank"
                    rel="noreferrer"
                    className="d-flex align-items-center justify-content-center"
                    style={{ height: 130, background: accent + "0f", borderBottom: "1px solid #eef1f7" }}
                  >
                    {url && isImage(url) ? (
                      <img src={url} alt={d.doc_name} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "cover" }} />
                    ) : (
                      <i className="bi bi-file-earmark-pdf" style={{ fontSize: 40, color: accent }}></i>
                    )}
                  </a>
                  <div className="p-3 d-flex flex-column gap-2 flex-grow-1">
                    <div className="d-flex align-items-start justify-content-between gap-2">
                      <div style={{ minWidth: 0 }}>
                        <div className="fw-bold" style={{ fontSize: 14, color: "#0f172a" }}>{show(d.doc_name)}</div>
                        <div style={{ fontSize: 11.5, color: "#94a3b8" }}>{titleCase(d.doc_type)}</div>
                      </div>
                      <Badge value={d.verification_status} />
                    </div>
                    <div className="d-flex flex-wrap gap-3" style={{ fontSize: 12 }}>
                      <div>
                        <div style={detailLabelStyle}>Number</div>
                        <div className="fw-semibold" style={{ color: d.doc_number ? "#0f172a" : "#cbd5e1" }}>{show(d.doc_number)}</div>
                      </div>
                      <div>
                        <div style={detailLabelStyle}>Uploaded</div>
                        <div className="fw-semibold" style={{ color: "#0f172a" }}>{formatDay(d.created_at)}</div>
                      </div>
                      {d.verified_at && (
                        <div>
                          <div style={detailLabelStyle}>Verified</div>
                          <div className="fw-semibold" style={{ color: "#0f172a" }}>{formatDay(d.verified_at)}</div>
                        </div>
                      )}
                      {d.expires_at && (
                        <div>
                          <div style={detailLabelStyle}>Expires</div>
                          <div className="fw-semibold" style={{ color: "#0f172a" }}>{formatDay(d.expires_at)}</div>
                        </div>
                      )}
                    </div>
                    {d.remarks && <div style={{ fontSize: 12, color: "#64748b" }}>{d.remarks}</div>}
                    {url && (
                      <a
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-sm mt-auto d-flex align-items-center justify-content-center gap-1"
                        style={{ background: accent + "1a", color: accent, border: "none", borderRadius: 8, fontSize: 12, fontWeight: 600 }}
                      >
                        <i className="bi bi-box-arrow-up-right"></i> Open Document
                      </a>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <Empty text="No documents uploaded" />
      )}
    </Section>
  );
};

/* ---------- Markup rules ---------- */
// fields shown in the expandable row (only non-zero values are listed)
const MARKUP_EXTRA_FIELDS = [
  ["total_commission", "Total Commission"],
  ["basic_commission", "Basic Commission"],
  ["yq_commission", "YQ Commission"],
  ["basic_plus_yq_commission", "Basic + YQ Commission"],
  ["plb_basic_commission", "PLB Basic Commission"],
  ["plb_basic_plus_yq_commission", "PLB Basic + YQ Commission"],
  ["cashback_amount", "Cashback"],
  ["gst", "GST"],
  ["service_charge", "Service Charge"],
  ["cancellation_fee", "Cancellation Fee"],
  ["change_fee", "Change Fee"],
];

const markupValue = (m) =>
  String(m.markup_amount_type).toLowerCase() == "percentage"
    ? `${Number(m.markup_amount)}%`
    : `₹ ${Number(m.markup_amount).toLocaleString("en-IN")}`;

export const MarkupsSection = ({ markups = [], accent = DETAIL_ACCENTS.amber }) => {
  const [open, setOpen] = useState({});
  const any = <span style={{ color: "#cbd5e1" }}>Any</span>;

  return (
    <Section icon="bi-sliders" title="Markup Rules" subtitle={`${plural(markups.length, "rule")} · blank criteria apply to all`} accent={accent}>
      {markups.length > 0 ? (
        <div className="table-responsive" style={detailTableWrap}>
          <table className="table table-sm align-middle mb-0" style={{ fontSize: 13 }}>
            <thead>
              <tr>
                {["Priority", "Airline", "Route Type", "Trip Type", "Cabin", "Fare Type", "Markup", "Applied On", "Restriction", "Status", "Charges"].map((h) => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {markups.map((m) => {
                const extras = MARKUP_EXTRA_FIELDS.filter(([key]) => Number(m[key] || 0) != 0);
                const isOpen = !!open[m.id];
                return (
                  <Fragment key={m.id}>
                    <tr>
                      <td style={tdStyle}><Pill value={show(m.priority)} accent={accent} /></td>
                      <td style={tdStyle}>
                        {m.airline ? (
                          <>
                            <div className="fw-semibold">{m.airline.airline_name}</div>
                            <div style={{ fontSize: 11, color: "#94a3b8" }}>{m.airline.airline_code}</div>
                          </>
                        ) : m.airline_code || any}
                      </td>
                      <td style={tdStyle}>{isEmpty(m.route_type) ? any : titleCase(m.route_type)}</td>
                      <td style={tdStyle}>{isEmpty(m.trip_type) ? any : titleCase(m.trip_type)}</td>
                      <td style={tdStyle}>{isEmpty(m.cabin_class) ? any : titleCase(m.cabin_class)}</td>
                      <td style={tdStyle}>{isEmpty(m.fare_type_code) ? any : m.fare_type_code}</td>
                      <td style={tdStyle}>
                        <span className="fw-bold" style={{ color: "#047857" }}>{markupValue(m)}</span>
                        <div style={{ fontSize: 11, color: "#94a3b8" }}>{titleCase(m.markup_amount_type)}</div>
                      </td>
                      <td style={tdStyle}>{humanOr(m.markup_applied_on, "-")}</td>
                      <td style={tdStyle}>{humanOr(m.restriction_on, "-")}</td>
                      <td style={tdStyle}><Badge value={m.status} /></td>
                      <td style={tdStyle}>
                        {extras.length > 0 ? (
                          <button
                            type="button"
                            className="btn btn-sm d-flex align-items-center gap-1"
                            style={{ background: accent + "1a", color: "#b45309", border: "none", borderRadius: 8, fontSize: 12, fontWeight: 600 }}
                            onClick={() => setOpen((prev) => ({ ...prev, [m.id]: !prev[m.id] }))}
                          >
                            <i className={"bi " + (isOpen ? "bi-chevron-up" : "bi-chevron-down")}></i>
                            {extras.length} set
                          </button>
                        ) : (
                          <span style={{ color: "#cbd5e1" }}>None</span>
                        )}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={11} style={{ background: "#fffbeb", padding: "12px 16px" }}>
                          <div className="row g-2">
                            {extras.map(([key, label]) => (
                              <div key={key} className="col-6 col-md-4 col-lg-3">
                                <div style={{ background: "#fff", border: "1px solid #fde68a", borderRadius: 8, padding: "8px 10px" }}>
                                  <div style={{ ...detailLabelStyle, marginBottom: 2 }}>{label}</div>
                                  <div className="fw-semibold" style={{ fontSize: 13, color: "#0f172a" }}>{Number(m[key]).toLocaleString("en-IN")}</div>
                                </div>
                              </div>
                            ))}
                            {m.ppp_segment_sector && (
                              <div className="col-12 col-md-6">
                                <div style={{ background: "#fff", border: "1px solid #fde68a", borderRadius: 8, padding: "8px 10px" }}>
                                  <div style={{ ...detailLabelStyle, marginBottom: 2 }}>Segment / Sector</div>
                                  <div className="fw-semibold" style={{ fontSize: 13, color: "#0f172a" }}>{m.ppp_segment_sector}</div>
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <Empty text="No markup rules" />
      )}
    </Section>
  );
};

/* ---------- Bank details ---------- */
export const BankDetailsSection = ({ banks = [], accent = DETAIL_ACCENTS.pink }) => {
  const [revealed, setRevealed] = useState({});

  return (
    <Section icon="bi-bank2" title="Bank Details" subtitle={plural(banks.length, "account")} accent={accent}>
      {banks.length > 0 ? (
        <div className="d-flex flex-column gap-3">
          {banks.map((b) => {
            const logo = fileUrl(b.bank_logo_url);
            const shown = !!revealed[b.id];
            return (
              <div key={b.id} style={{ border: "1px solid #eef1f7", borderRadius: 12, overflow: "hidden" }}>
                <div
                  className="d-flex align-items-center justify-content-between flex-wrap gap-2"
                  style={{ background: accent + "0f", borderBottom: "1px solid #eef1f7", padding: "12px 16px" }}
                >
                  <div className="d-flex align-items-center gap-2">
                    <span
                      className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
                      style={{ width: 40, height: 40, borderRadius: 10, background: "#fff", border: "1px solid #eef1f7", color: accent, fontSize: 18, overflow: "hidden" }}
                    >
                      {logo ? (
                        <img
                          src={logo}
                          alt=""
                          style={{ width: "100%", height: "100%", objectFit: "contain" }}
                          onError={(e) => { e.currentTarget.replaceWith(Object.assign(document.createElement("i"), { className: "bi bi-bank" })); }}
                        />
                      ) : <i className="bi bi-bank"></i>}
                    </span>
                    <div>
                      <div className="fw-bold" style={{ fontSize: 14.5, color: "#0f172a" }}>{show(b.bank_name)}</div>
                      <div style={{ fontSize: 11.5, color: "#94a3b8" }}>{[b.branch_name, humanOr(b.account_type, null)].filter(Boolean).join(" · ") || "-"}</div>
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    {b.is_primary && <Pill value="Primary" accent={accent} />}
                    <Badge value={b.status} />
                  </div>
                </div>
                <div style={{ padding: 16 }}>
                  <Fields
                    accent={accent}
                    items={[
                      { label: "Account Holder", value: b.account_holder_name, icon: "bi-person" },
                      {
                        label: "Account Number",
                        icon: "bi-credit-card-2-front",
                        value: isEmpty(b.account_number) ? null : (
                          <span className="d-inline-flex align-items-center gap-2">
                            <span style={{ letterSpacing: 0.5 }}>{shown ? b.account_number : maskAccount(b.account_number)}</span>
                            <button
                              type="button"
                              title={shown ? "Hide" : "Show"}
                              onClick={() => setRevealed((prev) => ({ ...prev, [b.id]: !prev[b.id] }))}
                              className="btn btn-sm p-0 border-0"
                              style={{ color: accent, lineHeight: 1 }}
                            >
                              <i className={"bi " + (shown ? "bi-eye-slash" : "bi-eye")}></i>
                            </button>
                          </span>
                        ),
                      },
                      { label: "IFSC Code", value: b.ifsc_code, icon: "bi-upc" },
                      { label: "MICR Code", value: b.micr_code, icon: "bi-upc-scan" },
                      { label: "SWIFT Code", value: b.swift_code, icon: "bi-globe" },
                      { label: "UPI ID", value: b.upi_id, icon: "bi-phone" },
                      { label: "PAN Number", value: b.pan_number, icon: "bi-person-vcard" },
                      { label: "Verified On", value: b.verification_date ? formatDate(b.verification_date) : null, icon: "bi-patch-check" },
                      {
                        label: "Cancelled Cheque",
                        icon: "bi-file-earmark-image",
                        value: b.cancelled_cheque_doc && (
                          <a href={fileUrl(b.cancelled_cheque_doc)} target="_blank" rel="noreferrer" style={linkStyle}>
                            View <i className="bi bi-box-arrow-up-right" style={{ fontSize: 11 }}></i>
                          </a>
                        ),
                      },
                      {
                        label: "Bank Address",
                        icon: "bi-geo-alt",
                        full: true,
                        value: [b.bank_address, b.city, b.state, b.pincode].filter((v) => !isEmpty(v)).join(", "),
                      },
                    ]}
                  />
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <Empty text="No bank accounts added" />
      )}
    </Section>
  );
};
